import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAliasMigration, AliasMigrationBanner } from '../hooks/useAliasMigration';
import StatsCard from './dashboard/StatsCard';
import QuickActions from './dashboard/QuickActions';
import RoomsGrid from './dashboard/RoomsGrid';
import ActivityFeed from './dashboard/ActivityFeed';
import LeaderboardWidget from './Leaderboard/LeaderboardWidget';
import gameRoomService from '../services/gameRoomService';
import { statisticsApi } from '../services/statisticsApi';

const ModernDashboard: React.FC = () => {
  const { user, isAuthenticated, refreshUserProfile } = useAuth();
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    gamesPlayed: 0,
    winRate: 0,
    activeRooms: 0,
    achievements: 0
  });
  
  // Auto-trigger alias migration on first login
  useAliasMigration();

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
      // Refresh user profile to get latest level
      refreshUserProfile().catch((err: Error) => console.error('Failed to refresh profile:', err));
    }
  }, [isAuthenticated, refreshUserProfile]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch rooms
      let roomsData: any[] = [];
      const roomsResponse = await gameRoomService.getAvailableRooms();
      if (roomsResponse.success && roomsResponse.data) {
        roomsData = (roomsResponse.data as any).rooms || [];
        setRooms(roomsData.map((room: any) => ({
          id: room._id || room.id,
          name: room.name,
          players: Array.isArray(room.players) ? room.players.length : 0,
          maxPlayers: room.maxPlayers || 4,
          status: room.status || 'waiting',
          gameType: room.gameType || 'Kadi ni Tidi',
          createdAt: room.createdAt || new Date().toISOString()
        })));
      }

      // Fetch user stats using statisticsApi for real-time aggregated data
      const userId = user?.id || user?._id;
      console.log('📊 [ModernDashboard] loadDashboardData - userId:', userId);
      
      if (userId) {
        try {
          console.log('📊 [ModernDashboard] Fetching stats for userId:', userId);
          const globalStats = await statisticsApi.getPlayerGlobalStats(userId);
          console.log('📊 [ModernDashboard] Received globalStats:', globalStats);
          
          setUserStats({
            gamesPlayed: globalStats.totalGamesPlayed || 0,
            winRate: Math.round(globalStats.overallWinRate || 0),
            activeRooms: roomsData?.length || 0,
            achievements: statisticsApi.calculateAchievements(globalStats).length || 0
          });
          console.log('📊 [ModernDashboard] Stats updated successfully');
        } catch (statsError) {
          console.error('❌ [ModernDashboard] Error fetching stats:', statsError);
          // Fallback to default values if stats API fails
          setUserStats({
            gamesPlayed: 0,
            winRate: 0,
            activeRooms: roomsData?.length || 0,
            achievements: 0
          });
        }
      } else {
        console.warn('⚠️ [ModernDashboard] No userId available');
        setUserStats({
          gamesPlayed: 0,
          winRate: 0,
          activeRooms: roomsData?.length || 0,
          achievements: 0
        });
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set defaults on error
      setUserStats({
        gamesPlayed: 0,
        winRate: 0,
        activeRooms: 0,
        achievements: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async (roomId: string) => {
    try {
      const response = await gameRoomService.joinRoom(roomId);
      if (response.success) {
        window.location.href = `/game/${roomId}`;
      }
    } catch (error) {
      console.error('Error joining room:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="modern-dashboard">
        <div className="dashboard-content">
          <div className="empty-state">
            <div className="empty-icon">🔒</div>
            <h2 className="empty-title">Authentication Required</h2>
            <p className="empty-message">Please log in to access your dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-dashboard">
      {/* Header removed - now using PersistentHeader from ResponsiveLayout */}
      
      <main className="dashboard-content dashboard-single-page">
        {/* Alias Migration Banner - shows if migration in progress or just completed */}
        <AliasMigrationBanner />
        
        <section className="welcome-section">
          <h1 className="welcome-greeting">Welcome back, {user?.username || 'Player'}!</h1>
          <p className="welcome-subtitle">Ready for your next game?</p>
        </section>
        
        <section className="stats-section">
          <div className="stats-grid stats-grid-3">
            <StatsCard icon="🎮" title="Games Played" value={userStats.gamesPlayed} subtitle="Total matches" />
            <StatsCard icon="🏆" title="Win Rate" value={`${userStats.winRate}%`} subtitle="Success ratio" />
            <StatsCard icon="⭐" title="Achievements" value={userStats.achievements} subtitle="Unlocked badges" />
          </div>
        </section>
        
        <section className="actions-section">
          <QuickActions />
        </section>
        
        <section className="rooms-section">
          <RoomsGrid 
            rooms={rooms}
            loading={loading}
            onJoinRoom={handleJoinRoom}
            onRefresh={loadDashboardData}
          />
        </section>
        
        <section className="leaderboard-section">
          <LeaderboardWidget 
            gameType="overall"
            period="weekly"
            limit={5}
            showViewAll={true}
          />
        </section>
        
        <section className="activity-section">
          <ActivityFeed maxItems={3} />
        </section>
      </main>
    </div>
  );
};

export default ModernDashboard;
